// ─── Collage Element: Text Block ──────────────────────────────
// Typewriter, editorial, or micro-label text.
// Applies customStyle overrides (font, size, color, align) when present.

import React from 'react';
import { motion } from 'framer-motion';
import { getElementFrameStyle } from '../../../lib/utils/collageLayout';
import { useCollageElementMotion } from '../../../lib/utils/collageMotion';

const STYLE_MAP = {
  'typewriter': ({ content, theme, cs }) => (
    <div
      className={!cs.fontSize ? 'text-[14px] leading-7 sm:text-[18px]' : undefined}
      style={{
        fontFamily: cs.fontFamily || theme.monoFont || 'monospace',
        color: cs.color || theme.textPrimary,
        fontSize: cs.fontSize || undefined,
        textAlign: cs.textAlign || undefined,
        lineHeight: cs.lineHeight || (cs.fontSize ? 1.6 : undefined),
        letterSpacing: cs.letterSpacing ? `${cs.letterSpacing}px` : undefined,
        textTransform: cs.textTransform || undefined,
        whiteSpace: 'pre-line',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
      }}
    >
      {content}
    </div>
  ),
  'typewriter-small-right': ({ content, theme, cs }) => (
    <div
      className={!cs.fontSize ? 'text-[12px] leading-6 sm:text-[15px]' : undefined}
      style={{
        fontFamily: cs.fontFamily || theme.monoFont || 'monospace',
        color: cs.color || theme.textSecondary,
        fontSize: cs.fontSize || undefined,
        textAlign: cs.textAlign || 'right',
        lineHeight: cs.lineHeight || (cs.fontSize ? 1.5 : undefined),
        letterSpacing: cs.letterSpacing ? `${cs.letterSpacing}px` : undefined,
        textTransform: cs.textTransform || undefined,
        whiteSpace: 'pre-line',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
      }}
    >
      {content}
    </div>
  ),
  'editorial-right': ({ content, theme, cs }) => (
    <div
      className={!cs.fontSize ? 'text-[18px] leading-[1.7] sm:text-[30px]' : undefined}
      style={{
        fontFamily: cs.fontFamily || theme.headingFont,
        color: cs.color || theme.textPrimary,
        fontSize: cs.fontSize || undefined,
        textAlign: cs.textAlign || 'right',
        lineHeight: cs.lineHeight || (cs.fontSize ? 1.5 : undefined),
        letterSpacing: cs.letterSpacing ? `${cs.letterSpacing}px` : undefined,
        textTransform: cs.textTransform || undefined,
        whiteSpace: 'pre-line',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
      }}
    >
      {content}
    </div>
  ),
  'micro-label': ({ content, theme, cs }) => (
    <div
      className={!cs.fontSize ? 'text-[10px] sm:text-[11px]' : undefined}
      style={{
        fontFamily: cs.fontFamily || theme.monoFont || theme.bodyFont,
        color: cs.color || 'rgba(255,255,255,0.88)',
        fontSize: cs.fontSize || undefined,
        textAlign: cs.textAlign || 'left',
        textTransform: cs.textTransform || 'uppercase',
        letterSpacing: cs.letterSpacing ? `${cs.letterSpacing}px` : '0.18em',
        lineHeight: cs.lineHeight || undefined,
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
      }}
    >
      {content}
    </div>
  ),
  'micro-label-center': ({ content, theme, cs }) => (
    <div
      className={!cs.fontSize ? 'text-[10px] sm:text-[11px]' : undefined}
      style={{
        fontFamily: cs.fontFamily || theme.monoFont || theme.bodyFont,
        color: cs.color || 'rgba(255,255,255,0.88)',
        fontSize: cs.fontSize || undefined,
        textAlign: cs.textAlign || 'center',
        textTransform: cs.textTransform || 'uppercase',
        letterSpacing: cs.letterSpacing ? `${cs.letterSpacing}px` : '0.18em',
        lineHeight: cs.lineHeight || undefined,
      }}
    >
      {content}
    </div>
  ),
  'micro-label-right': ({ content, theme, cs }) => (
    <div
      className={!cs.fontSize ? 'text-[10px] sm:text-[11px]' : undefined}
      style={{
        fontFamily: cs.fontFamily || theme.monoFont || theme.bodyFont,
        color: cs.color || 'rgba(255,255,255,0.88)',
        fontSize: cs.fontSize || undefined,
        textAlign: cs.textAlign || 'right',
        textTransform: cs.textTransform || 'uppercase',
        letterSpacing: cs.letterSpacing ? `${cs.letterSpacing}px` : '0.18em',
        lineHeight: cs.lineHeight || undefined,
      }}
    >
      {content}
    </div>
  ),
  'editorial-east-asian': ({ content, theme, cs }) => (
    <div
      className={!cs.fontSize ? 'text-[18px] leading-[1.75] sm:text-[28px]' : undefined}
      style={{
        fontFamily: cs.fontFamily || theme.eastAsianFont || '"Noto Serif SC", serif',
        color: cs.color || theme.textPrimary,
        fontSize: cs.fontSize || undefined,
        textAlign: cs.textAlign || 'right',
        lineHeight: cs.lineHeight || (cs.fontSize ? 1.6 : undefined),
        letterSpacing: cs.letterSpacing ? `${cs.letterSpacing}px` : undefined,
        textTransform: cs.textTransform || undefined,
        whiteSpace: 'pre-line',
      }}
    >
      {content}
    </div>
  ),
};

export default function TextBlock({ element, isMobile, theme, skipPositioning, isEditing, onEditText }) {
  const style = skipPositioning ? {} : getElementFrameStyle(element.layout, isMobile);
  const motionProps = useCollageElementMotion(element);
  const Renderer = STYLE_MAP[element.style];
  if (!Renderer) return null;
  const cs = element.customStyle || {};

  const handleBlur = (e) => {
    if (onEditText) {
      onEditText('content', e.currentTarget.innerText);
    }
  };

  return (
    <motion.div {...motionProps} style={style} className={isEditing ? 'select-text' : 'select-none'}>
      {isEditing ? (
        <div
          contentEditable
          suppressContentEditableWarning
          onBlur={handleBlur}
          onKeyDown={e => { if (e.key === 'Escape') e.currentTarget.blur(); }}
          className="outline-none ring-1 ring-blue-400/50 rounded px-1 -mx-1"
          style={{
            fontFamily: cs.fontFamily || theme.monoFont || 'monospace',
            color: cs.color || theme.textPrimary,
            fontSize: cs.fontSize || undefined,
            textAlign: cs.textAlign || undefined,
            lineHeight: cs.lineHeight || (cs.fontSize ? 1.6 : undefined),
            letterSpacing: cs.letterSpacing ? `${cs.letterSpacing}px` : undefined,
            textTransform: cs.textTransform || undefined,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
            minHeight: '1em',
          }}
          data-testid={`inline-edit-${element.id}`}
        >
          {element.content}
        </div>
      ) : (
        <Renderer content={element.content} theme={theme} cs={cs} />
      )}
    </motion.div>
  );
}
